import Grid from '@mui/material/Grid';
import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete';
import { segmentIdsPlaceholder } from '@openx/types/targeting/contextualSegments';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BulkAddDrawer } from '../../shared/BulkAdd/BulkAddDrawer';
import type { ValidationResult } from '../../shared/BulkAdd/types';
import { formatBulkContextualSegmentForDisplay } from '../utils/bulkAdd';
import { CONTEXTUAL_INCLUSION_TYPES, type ContextualInclusionType } from '../utils/enrichment';

type ContextualSegmentsBulkDrawerProps = {
  onClose: () => void;
  onApply: (data: { serializedSegments: string[]; inclusionType: ContextualInclusionType }) => void;
  validateBulkSegments: (segmentIds: string[], inclusionType: ContextualInclusionType) => Promise<ValidationResult>;
};

export const ContextualSegmentsBulkDrawer = ({
  onClose,
  onApply,
  validateBulkSegments,
}: ContextualSegmentsBulkDrawerProps) => {
  const { t } = useTranslation();
  const [inclusionType, setInclusionType] = useState<ContextualInclusionType>('includes');

  const inclusionLabels: Record<ContextualInclusionType, string> = useMemo(
    () => ({
      includes: t('Include'),
      excludes: t('Exclude'),
    }),
    [t]
  );

  const getOptionLabel = useCallback((option: ContextualInclusionType) => inclusionLabels[option], [inclusionLabels]);

  const handleInclusionTypeChange = useCallback((_e: unknown, value: ContextualInclusionType) => {
    setInclusionType(value);
  }, []);

  const validate = useCallback(
    (segmentIds: string[]): Promise<ValidationResult> => validateBulkSegments(segmentIds, inclusionType),
    [validateBulkSegments, inclusionType]
  );

  const handleApply = useCallback(
    (serializedSegments: string[]) => {
      onApply({ serializedSegments, inclusionType });
    },
    [onApply, inclusionType]
  );

  return (
    <BulkAddDrawer
      onClose={onClose}
      onApply={handleApply}
      placeholder={segmentIdsPlaceholder}
      validate={validate}
      childParams={inclusionType}
      formatForDisplay={formatBulkContextualSegmentForDisplay}
    >
      <Grid container spacing={1}>
        <Grid size={12}>
          <AutoComplete
            options={CONTEXTUAL_INCLUSION_TYPES}
            textFieldProps={{ label: t('Mode') }}
            renderOptions={{
              dataTest: 'inclusion-type-option',
            }}
            getOptionLabel={getOptionLabel}
            textFieldReadOnly
            disableClearable
            onChange={handleInclusionTypeChange}
            value={inclusionType}
            data-test="contextual-segments-inclusion-type"
            fullWidth
          />
        </Grid>
      </Grid>
    </BulkAddDrawer>
  );
};
